import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';
import { useActiveCompany } from '@/lib/active-company-context';

import { providerGrantsResponseSchema, type ProviderGrant } from './types';

const KEY = 'provider-grants';

interface GrantScope {
  grants: { module: string; id: string; actions: string[] }[];
}
export interface GrantInput {
  userId: string;
  expiresAt: string;
  reason: string;
  scope: GrantScope;
}

export interface InviteInput {
  email: string;
  firstName: string;
  lastName: string;
  expiresAt: string;
  reason: string;
  scope: GrantScope;
}
export interface InviteResult {
  userId: string;
  grantId: string;
  account: 'created' | 'linked' | 'existing';
  invitation: 'sent' | 'failed';
}
interface Envelope<T> {
  data: T;
}

/** Accesos temporales de proveedor de la empresa activa. queryKey por empresa. */
export function useProviderGrants({
  enabled,
}: {
  enabled: boolean;
}): UseQueryResult<ProviderGrant[]> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: [KEY, activeCompanyId],
    queryFn: async () => {
      const raw = await api.get<unknown>('/api/v1/admin/provider-grants');
      return providerGrantsResponseSchema.parse(raw).data;
    },
    enabled: enabled && Boolean(activeCompanyId),
  });
}

export function useGrantAccess(): ReturnType<typeof useMutation<void, Error, GrantInput>> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      await api.post('/api/v1/admin/provider-grants', input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useRevokeGrant(): ReturnType<typeof useMutation<void, Error, { id: string }>> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      await api.post(`/api/v1/admin/provider-grants/${id}/revoke`, {});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Invita a un proveedor SIN usuario: el backend crea su cuenta y le otorga el acceso. */
export function useInviteProvider(): ReturnType<
  typeof useMutation<InviteResult, Error, InviteInput>
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      (await api.post<Envelope<InviteResult>>('/api/v1/admin/provider-grants/invite', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Reenvía el enlace para crear la contraseña a un proveedor externo. */
export function useResendInvitation(): ReturnType<
  typeof useMutation<'sent' | 'failed', Error, { userId: string }>
> {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ userId }) =>
      (
        await api.post<Envelope<{ invitation: 'sent' | 'failed' }>>(
          `/api/v1/admin/provider-grants/invite/${userId}/resend`,
          {},
        )
      ).data.invitation,
  });
}
